// Browser-local ASR (Moonshine Base), loaded on demand from CDN; audio never
// leaves the device. new Function() import keeps Webpack 3 from bundling the URL.

import { isIOS } from './micStream'

var CDN_URL = 'https://cdn.jsdelivr.net/npm/@moonshine-ai/moonshine-js@latest/dist/moonshine.min.js'
var MODEL_ID = 'model/base'
var TARGET_SAMPLE_RATE = 16000
// Over ~5 s generate() silently returns "", so trailing VAD silence is trimmed then capped.
var MAX_SAMPLES = 80000 // 5 s at 16 kHz

// Must match what MoonshineModel.load() fetches (BASE_ASSET_PATH + MODEL_ID +
// '/quantized/'), or the prefetch warms nothing and the model downloads twice.
var MODEL_ASSET_BASE_URL = 'https://download.moonshine.ai/' + MODEL_ID + '/quantized/'
var MODEL_ASSET_FILES = ['encoder_model.onnx', 'decoder_model_merged.onnx']

var _model = null
var _loadPromise = null
var _progressListeners = []
var _state = { status: 'idle', loaded: 0, total: 0, cached: false }

function dynamicImport (url) {
  return new Function('u', 'return import(u)')(url) // eslint-disable-line no-new-func
}

// iOS WebKit can't allocate onnxruntime-web's shared wasm memory: every load
// ends in "no available backend found. ERR: [wasm] RangeError: Out of memory"
// (microsoft/onnxruntime#22086), same wall as vadSilero.
export function isSupported () {
  if (isIOS()) return false
  var hasAudioCtx = typeof AudioContext !== 'undefined' || typeof webkitAudioContext !== 'undefined'
  if (!hasAudioCtx) return false
  try {
    new Function('return import("")') // eslint-disable-line no-new-func
    return true
  } catch (e) {
    return false
  }
}

export function getModelState () {
  return _state
}

export function subscribeProgress (cb) {
  _progressListeners.push(cb)
  cb(_state)
}

export function unsubscribeProgress (cb) {
  var i = _progressListeners.indexOf(cb)
  if (i !== -1) _progressListeners.splice(i, 1)
}

function setState (patch) {
  var next = {}
  for (var k in _state) next[k] = _state[k]
  for (var k2 in patch) next[k2] = patch[k2]
  _state = next
  _progressListeners.forEach(function (cb) { cb(_state) })
}

function sumOf (arr) {
  var total = 0
  for (var i = 0; i < arr.length; i++) total += arr[i]
  return total
}

// Best effort: a prefetch failure still lets loadModel() try the real load.
function prefetchAssets () {
  var loaded = MODEL_ASSET_FILES.map(function () { return 0 })
  var total = MODEL_ASSET_FILES.map(function () { return 0 })
  var allCacheHits = true

  setState({ status: 'downloading', loaded: 0, total: 0, cached: false })

  function reportProgress () {
    setState({ status: 'downloading', loaded: sumOf(loaded), total: sumOf(total) })
  }

  return Promise.all(MODEL_ASSET_FILES.map(function (file, i) {
    var url = MODEL_ASSET_BASE_URL + file
    var startedAt = Date.now()
    return fetch(url).then(function (res) {
      if (!res.ok) throw new Error('model asset ' + file + ' responded ' + res.status)
      var lengthHeader = res.headers.get('content-length')
      total[i] = lengthHeader ? parseInt(lengthHeader, 10) : 0
      reportProgress()

      if (!res.body || !res.body.getReader) {
        // older Safari: no streaming
        return res.arrayBuffer().then(function (buf) {
          loaded[i] = buf.byteLength
          if (Date.now() - startedAt > 300) allCacheHits = false
          reportProgress()
        })
      }

      var reader = res.body.getReader()
      function pump () {
        return reader.read().then(function (result) {
          if (result.done) {
            if (Date.now() - startedAt > 300) allCacheHits = false
            return
          }
          loaded[i] += result.value.length
          reportProgress()
          return pump()
        })
      }
      return pump()
    })
  })).then(function () {
    setState({ cached: allCacheHits })
  })
}

export function preload () {
  loadModel().catch(function () {})
}

export function retry () {
  _loadPromise = null
  _model = null
  return preload()
}

function loadModel () {
  if (_loadPromise) return _loadPromise
  _loadPromise = prefetchAssets()
    .catch(function () { /* best-effort progress only; still try the real load */ })
    .then(function () {
      return dynamicImport(CDN_URL)
    })
    .then(function (Moonshine) {
      var model = new Moonshine.MoonshineModel(MODEL_ID)
      return model.loadModel().then(function () {
        _model = model
        var known = _state.total || _state.loaded
        setState({ status: 'ready', loaded: known, total: known })
        return model
      })
    })
    .catch(function (err) {
      console.error('[asrMoonshine] load failed:', err)
      _loadPromise = null
      setState({ status: 'error', error: String((err && err.message) || err) })
      throw err
    })
  return _loadPromise
}

async function blobToFloat32 (blob) {
  var arrayBuffer = await blob.arrayBuffer()
  var AudioCtx = typeof AudioContext !== 'undefined' ? AudioContext : webkitAudioContext
  var decodeCtx = new AudioCtx()
  var audioBuffer
  try {
    audioBuffer = await decodeCtx.decodeAudioData(arrayBuffer)
  } finally {
    decodeCtx.close()
  }
  var targetLength = Math.ceil(audioBuffer.duration * TARGET_SAMPLE_RATE)
  if (targetLength === 0) return new Float32Array(0)
  var offlineCtx = new OfflineAudioContext(1, targetLength, TARGET_SAMPLE_RATE)
  var source = offlineCtx.createBufferSource()
  source.buffer = audioBuffer
  source.connect(offlineCtx.destination)
  source.start(0)
  var resampled = await offlineCtx.startRendering()
  var ch = resampled.getChannelData(0)
  var maxAbs = 0
  var sum = 0
  for (var i = 0; i < ch.length; i++) {
    var v = Math.abs(ch[i])
    if (v > maxAbs) maxAbs = v
    sum += v
  }
  console.log('[asrMoonshine] decoded maxAbs=' + maxAbs.toFixed(5) + ' meanAbs=' + (sum / ch.length).toFixed(5) + ' channels=' + resampled.numberOfChannels + ' dur=' + resampled.duration.toFixed(2) + 's')
  return ch
}

export async function transcribe (blob) {
  if (!blob || !blob.size) return ''

  var model = _model
  if (!model) {
    model = await loadModel()
  }

  var samples
  try {
    samples = await blobToFloat32(blob)
  } catch (e) {
    console.error('[asrMoonshine] blobToFloat32 failed:', e)
    throw e
  }
  console.log('[asrMoonshine] blob:', blob.size, 'bytes  samples:', samples.length, 'at 16kHz')
  if (samples.length === 0) return ''

  samples = normalizeLevel(samples)
  samples = trimAndCap(samples)
  var trimMax = 0
  for (var ti = 0; ti < samples.length; ti++) { var ta = Math.abs(samples[ti]); if (ta > trimMax) trimMax = ta }
  console.log('[asrMoonshine] after normalize+trim+cap: samples=' + samples.length + ' (' + (samples.length / TARGET_SAMPLE_RATE).toFixed(2) + 's) maxAbs=' + trimMax.toFixed(5))

  var text = await model.generate(samples)
  console.log('[asrMoonshine] generate() returned:', JSON.stringify(text))
  return normalizeTranscript(text)
}

// RMS, not peak: attempt audio is peaky (peak 0.966 vs mean 0.026) and peak
// normalising left speech too quiet, which Moonshine returns as "". Moonshine
// reads the raw waveform, so level matters. 0.09 = rms of static/test-speech.wav.
var TARGET_RMS = 0.09

var PEAK_CEILING = 0.95

function measureLevel (samples) {
  var sumSquares = 0
  var maxAbs = 0
  for (var i = 0; i < samples.length; i++) {
    var s = samples[i]
    sumSquares += s * s
    var a = s < 0 ? -s : s
    if (a > maxAbs) maxAbs = a
  }
  return {
    rms: samples.length > 0 ? Math.sqrt(sumSquares / samples.length) : 0,
    maxAbs: maxAbs
  }
}

export function normalizeLevel (samples) {
  if (samples.length === 0) return samples

  var level = measureLevel(samples)

  if (level.maxAbs < 0.001 || level.rms < 0.0005) return samples

  var scale = TARGET_RMS / level.rms
  // The ceiling only reduces the RMS gain, so a transient can't set the level.
  if (level.maxAbs * scale > PEAK_CEILING) scale = PEAK_CEILING / level.maxAbs

  var out = new Float32Array(samples.length)
  for (var j = 0; j < samples.length; j++) out[j] = samples[j] * scale
  return out
}

function trimAndCap (samples) {
  // Relative to RMS: a peak-relative floor eats speech on peaky clips, an
  // absolute one trims everything on quiet mics.
  var SILENCE_FLOOR = Math.max(0.01, measureLevel(samples).rms * 0.5)
  var TAIL_PAD = Math.round(TARGET_SAMPLE_RATE * 0.5) // 500 ms
  var last = samples.length - 1
  while (last > 0 && Math.abs(samples[last]) < SILENCE_FLOOR) last--
  var end = Math.min(samples.length, last + TAIL_PAD)
  var trimmed = samples.length - end > 0 ? samples.slice(0, end) : samples

  // Keep the head: Moonshine hallucinates from a mid-sentence entry point.
  if (trimmed.length > MAX_SAMPLES) {
    trimmed = trimmed.slice(0, MAX_SAMPLES)
  }
  return trimmed
}

// Bisect for generate() returning '': runs static/test-speech.wav through the attempt path.
// Text back = the captured audio is the problem; '' = model/API broken. Console: window.__asrSelfTest()
export async function selfTest (url) {
  var src = url || '/static/test-speech.wav'
  var response = await fetch(src)
  if (!response.ok) throw new Error('selfTest: could not fetch ' + src + ' (' + response.status + ')')
  var blob = await response.blob()
  console.log('[asrMoonshine selfTest] fetched ' + blob.size + ' bytes from ' + src)
  var text = await transcribe(blob)
  console.log('[asrMoonshine selfTest] RESULT: ' + JSON.stringify(text) +
    (text ? '  -> model OK, suspect the captured audio' : '  -> model/API broken, not the recording'))
  return text
}

if (typeof window !== 'undefined') {
  window.__asrSelfTest = selfTest
}

function normalizeTranscript (text) {
  if (!text || typeof text !== 'string') return ''
  return text
    .replace(/\[[^\]]*\]|\([^)]*\)|\*[^*]*\*/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}
